import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useAuth } from "@/lib/auth";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { ShieldCheck, FlaskConical, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { listSignups, setTestAccountFlag, type SignupRow } from "@/lib/admin-signups.functions";

export const Route = createFileRoute("/_authenticated/admin/signups")({
  component: AdminSignupsPage,
  head: () => ({ meta: [{ title: "Signups — Platform Admin" }] }),
});

function AdminSignupsPage() {
  const { isPlatformAdmin } = useAuth();
  const list = useServerFn(listSignups);
  const setFlag = useServerFn(setTestAccountFlag);
  const [rows, setRows] = useState<SignupRow[]>([]);
  const [totals, setTotals] = useState({ all: 0, test: 0, real: 0 });
  const [showTest, setShowTest] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async (include: boolean) => {
    setLoading(true);
    try {
      const res = await list({ data: { includeTestAccounts: include, limit: 200 } });
      setRows(res.rows);
      setTotals(res.totals);
    } catch (e: any) {
      toast.error(e?.message ?? "Failed to load signups");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isPlatformAdmin) load(showTest);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPlatformAdmin, showTest]);

  if (!isPlatformAdmin) {
    return (
      <div className="px-4 py-10 md:px-8">
        <Card className="mx-auto max-w-lg p-8 text-center">
          <ShieldCheck className="mx-auto mb-3 h-10 w-10 text-muted-foreground" />
          <h1 className="text-lg font-semibold">Restricted</h1>
          <p className="mt-2 text-sm text-muted-foreground">Platform admins only.</p>
        </Card>
      </div>
    );
  }

  const toggleFlag = async (row: SignupRow) => {
    try {
      await setFlag({ data: { profileId: row.id, isTestAccount: !row.is_test_account } });
      toast.success(row.is_test_account ? "Unmarked as test account" : "Marked as test account");
      await load(showTest);
    } catch (e: any) {
      toast.error(e?.message ?? "Failed to update");
    }
  };

  const hiddenCount = totals.test;

  return (
    <div className="px-4 py-6 md:px-8 md:py-8">
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <p className="text-xs font-medium uppercase tracking-wider text-primary">Platform Admin</p>
          <h1 className="font-display text-2xl font-bold md:text-3xl">Signups</h1>
          <p className="text-sm text-muted-foreground">
            {totals.real} real {totals.real === 1 ? "user" : "users"} · {totals.test} test{" "}
            {totals.test === 1 ? "account" : "accounts"} flagged
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 rounded-md border border-border bg-card px-3 py-2">
            <FlaskConical className="h-4 w-4 text-muted-foreground" />
            <Label htmlFor="show-test" className="cursor-pointer text-xs font-medium">
              Show test accounts
            </Label>
            <Switch id="show-test" checked={showTest} onCheckedChange={setShowTest} />
          </div>
          <Button size="sm" variant="outline" onClick={() => load(showTest)} disabled={loading}>
            <RefreshCw className={`mr-1 h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
        </div>
      </div>

      {!showTest && hiddenCount > 0 && (
        <div className="mb-4 rounded-md border border-border bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
          {hiddenCount} test {hiddenCount === 1 ? "account is" : "accounts are"} hidden. Flip the toggle to include them.
        </div>
      )}

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-3 py-2 text-left">Email</th>
                <th className="px-3 py-2 text-left">Name</th>
                <th className="px-3 py-2 text-left">Signed up</th>
                <th className="px-3 py-2 text-left">Last sign-in</th>
                <th className="px-3 py-2 text-left">Flag</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={6} className="px-3 py-6 text-center text-muted-foreground">Loading…</td></tr>
              )}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={6} className="px-3 py-6 text-center text-muted-foreground">No signups to show.</td></tr>
              )}
              {!loading && rows.map((r) => (
                <tr key={r.id} className="border-t border-border">
                  <td className="px-3 py-2 font-medium">{r.email ?? "—"}</td>
                  <td className="px-3 py-2 text-muted-foreground">{r.full_name ?? "—"}</td>
                  <td className="px-3 py-2 text-muted-foreground">{new Date(r.created_at).toLocaleString()}</td>
                  <td className="px-3 py-2 text-muted-foreground">
                    {r.last_sign_in_at ? new Date(r.last_sign_in_at).toLocaleString() : "Never"}
                  </td>
                  <td className="px-3 py-2">
                    {r.is_test_account ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-amber-500/10 px-2 py-0.5 text-xs font-medium text-amber-600 dark:text-amber-400">
                        <FlaskConical className="h-3 w-3" /> Test
                      </span>
                    ) : (
                      <span className="text-xs text-muted-foreground">Real</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <Button size="sm" variant="ghost" onClick={() => toggleFlag(r)}>
                      {r.is_test_account ? "Unmark" : "Mark as test"}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
